import type { DashboardFilter, DateRange } from '@/domain/repositories/dashboard.repository'
import type {
  CategorySalesDto,
  DashboardOverviewDto,
  HourlySalesDto,
  KpiMetricDto,
  OrderDto,
  OutletSalesDto,
  ProductSalesRankDto,
  RevenuePointDto,
} from './dashboard.dto'

const RANGE_DAYS: Record<string, number> = { today: 1, '7d': 7, '30d': 30, '90d': 90 }

/** PRNG deterministik (mulberry32) supaya data mock konsisten. */
function seeded(seed: number) {
  return () => {
    seed |= 0
    seed = (seed + 0x6d2b79f5) | 0
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const OUTLETS_MASTER = [
  { id: 'uuid-outlet-1', name: 'Cabang Utama', weight: 0.4 },
  { id: 'uuid-outlet-2', name: 'Cabang Selatan', weight: 0.25 },
  { id: 'uuid-outlet-3', name: 'Cabang Timur', weight: 0.18 },
  { id: 'uuid-outlet-4', name: 'Kios Mall', weight: 0.12 },
  { id: 'uuid-outlet-5', name: 'Booth Event', weight: 0.05 },
]

const CATEGORIES_MASTER = [
  { id: 'cat-1', name: 'Makanan Utama', weight: 0.45 },
  { id: 'cat-2', name: 'Minuman', weight: 0.3 },
  { id: 'cat-3', name: 'Snack & Cemilan', weight: 0.13 },
  { id: 'cat-4', name: 'Dessert', weight: 0.08 },
  { id: 'cat-5', name: 'Retail / Merchandise', weight: 0.04 },
]

const CUSTOMERS = [
  'Budi Santoso',
  'Siti Rahma',
  'Andi Wijaya',
  'Dewi Lestari',
  'Rudi Hartono',
  'Maya Putri',
  'Tumbuh Sejahtera Co',
  'Walk-in',
]
const STATUSES = ['completed', 'completed', 'completed', 'pending', 'refunded', 'cancelled']
const PAYMENTS = ['qris', 'cash', 'card', 'ewallet']

function calculateDays(filter?: DashboardFilter): number {
  if (!filter) return 1
  if (filter.period !== 'custom') {
    return RANGE_DAYS[filter.period] ?? 1
  }
  if (filter.startDate && filter.endDate) {
    const start = new Date(filter.startDate)
    const end = new Date(filter.endDate)
    const diffTime = Math.abs(end.getTime() - start.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    return Math.max(diffDays, 1)
  }
  return 1
}

/** Hitung scale factor unik & deterministik per-outlet ID (UUID asli maupun mock). */
function getOutletScale(outletId?: string | null): number {
  if (!outletId || outletId === 'all') return 1.0
  const found = OUTLETS_MASTER.find((o) => o.id === outletId)
  if (found) return found.weight

  // Hash String UUID agar setiap outlet asli memiliki persentase omset unik (15% s/d 65%)
  let hash = 0
  for (let i = 0; i < outletId.length; i++) {
    hash = (hash << 5) - hash + outletId.charCodeAt(i)
    hash |= 0
  }
  const pct = (Math.abs(hash) % 50) + 15
  return pct / 100
}

export const dashboardFixtures = {
  overview(filter?: DashboardFilter): DashboardOverviewDto {
    const days = calculateDays(filter)
    const selectedOutletId = filter?.outletId ?? null
    const periodKey = filter?.period ?? 'today'
    const scaleFactor = getOutletScale(selectedOutletId)

    // Hash unik untuk PRNG seed per kombinasi outlet & days
    let outletSeed = 0
    if (selectedOutletId) {
      for (let i = 0; i < selectedOutletId.length; i++) {
        outletSeed += selectedOutletId.charCodeAt(i)
      }
    }

    const rand = seeded(days * 31 + outletSeed * 17)
    const revenueTrend: RevenuePointDto[] = []
    const today = new Date()

    let totalRevenue = 0
    let totalTransactions = 0

    for (let i = days - 1; i >= 0; i--) {
      const d = new Date(today)
      d.setDate(today.getDate() - i)
      const isWeekend = d.getDay() === 0 || d.getDay() === 6
      const baseRev = (5_000_000 + rand() * 3_500_000 + (isWeekend ? 2_500_000 : 0)) * scaleFactor
      const rev = Math.round(baseRev)
      const tx = Math.round(rev / (45_000 + rand() * 15_000))

      totalRevenue += rev
      totalTransactions += tx

      revenueTrend.push({
        date: d.toISOString().slice(0, 10),
        revenue: rev,
        transactions: tx,
      })
    }

    // Products Sold Breakdown (Section 3)
    const singleQty = Math.round(totalTransactions * 1.8)
    const packageQty = Math.round(totalTransactions * 0.35)
    const componentQty = Math.round(packageQty * 2.2)
    const totalProductsSold = singleQty + packageQty + componentQty

    // Category Sales Breakdown (Section 5) - Konsisten 100% dengan totalRevenue
    let remainingCategoryRev = totalRevenue
    const categorySales: CategorySalesDto[] = CATEGORIES_MASTER.map((cat, index) => {
      let catRev = 0
      if (index === CATEGORIES_MASTER.length - 1) {
        catRev = remainingCategoryRev
      } else {
        catRev = Math.round(totalRevenue * cat.weight)
        remainingCategoryRev -= catRev
      }

      const catShare = Math.round((catRev / Math.max(totalRevenue, 1)) * 1000) / 10
      const match = selectedOutletId ? OUTLETS_MASTER.find((o) => o.id === selectedOutletId) : null
      const currentOutletName = match
        ? match.name
        : selectedOutletId && selectedOutletId !== 'all'
          ? `Outlet (${selectedOutletId.slice(0, 8)})`
          : 'Semua Outlet'

      return {
        category_id: cat.id,
        category: cat.name,
        category_name: cat.name,
        outlet_name: currentOutletName,
        outlet: currentOutletName,
        revenue: catRev,
        total_qty: Math.round(catRev / 35_000),
        share: catShare,
      }
    })

    // Outlet Sales Breakdown (Section 6)
    let outletSales: OutletSalesDto[] = []
    if (selectedOutletId && selectedOutletId !== 'all') {
      const match = OUTLETS_MASTER.find((o) => o.id === selectedOutletId)
      const name = match ? match.name : `Outlet (${selectedOutletId.slice(0, 8)})`
      outletSales = [
        {
          outlet_id: selectedOutletId,
          outlet: name,
          revenue: totalRevenue,
          transactions: totalTransactions,
          share: 100,
        },
      ]
    } else {
      let remainingOutletRev = totalRevenue
      let remainingOutletTx = totalTransactions
      outletSales = OUTLETS_MASTER.map((out, index) => {
        let oRev = 0
        let oTx = 0
        if (index === OUTLETS_MASTER.length - 1) {
          oRev = remainingOutletRev
          oTx = remainingOutletTx
        } else {
          oRev = Math.round(totalRevenue * out.weight)
          oTx = Math.round(totalTransactions * out.weight)
          remainingOutletRev -= oRev
          remainingOutletTx -= oTx
        }
        const share = Math.round((oRev / Math.max(totalRevenue, 1)) * 1000) / 10
        return {
          outlet_id: out.id,
          outlet: out.name,
          revenue: oRev,
          transactions: oTx,
          share,
        }
      })
    }

    const hourlySales: HourlySalesDto[] = Array.from({ length: 24 }, (_, h) => {
      let weight = 0.01
      if (h >= 11 && h <= 14) weight = 0.15
      else if (h >= 17 && h <= 20) weight = 0.12
      else if (h >= 8 && h <= 21) weight = 0.05

      const hTx = Math.round(totalTransactions * weight)
      const hRev = Math.round(totalRevenue * weight)
      return {
        hour: h,
        hour_label: `${String(h).padStart(2, '0')}:00`,
        revenue: hRev,
        transactions: hTx,
      }
    })

    const defaultOutletName = selectedOutletId
      ? OUTLETS_MASTER.find((o) => o.id === selectedOutletId)?.name || 'Cabang Terpilih'
      : 'Cabang Utama'

    const top_products: ProductSalesRankDto[] = [
      { product_id: 'p-1', product_name: 'Ayam Goreng Lengkuas', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.bogor', category_name: 'Makanan Utama', current_stock: 45, stock_active: true, total_qty: Math.round(180 * scaleFactor), revenue: Math.round(4500000 * scaleFactor) },
      { product_id: 'p-2', product_name: 'Es Kopi Susu Gula Aren', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.bogor', category_name: 'Minuman', current_stock: 120, stock_active: true, total_qty: Math.round(155 * scaleFactor), revenue: Math.round(3100000 * scaleFactor) },
      { product_id: 'p-3', product_name: 'Nasi Goreng Spesial', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.sudirman', category_name: 'Makanan Utama', current_stock: 60, stock_active: true, total_qty: Math.round(130 * scaleFactor), revenue: Math.round(3900000 * scaleFactor) },
      { product_id: 'p-4', product_name: 'Es Teh Manis Jumbo', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.bogor', category_name: 'Minuman', current_stock: 300, stock_active: true, total_qty: Math.round(210 * scaleFactor), revenue: Math.round(1680000 * scaleFactor) },
      { product_id: 'p-5', product_name: 'Paket Bebek Bakar Komplit', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.bandung', category_name: 'Makanan Utama', current_stock: 25, stock_active: true, total_qty: Math.round(95 * scaleFactor), revenue: Math.round(4275000 * scaleFactor) },
      { product_id: 'p-6', product_name: 'Kentang Goreng Keju', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.sudirman', category_name: 'Snack & Cemilan', current_stock: 50, stock_active: true, total_qty: Math.round(88 * scaleFactor), revenue: Math.round(1760000 * scaleFactor) },
      { product_id: 'p-7', product_name: 'Mie Godhog Jawa', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.bandung', category_name: 'Makanan Utama', current_stock: 35, stock_active: true, total_qty: Math.round(82 * scaleFactor), revenue: Math.round(2050000 * scaleFactor) },
      { product_id: 'p-8', product_name: 'Pisang Bakar Coklat', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.bogor', category_name: 'Dessert', current_stock: 40, stock_active: true, total_qty: Math.round(75 * scaleFactor), revenue: Math.round(1350000 * scaleFactor) },
      { product_id: 'p-9', product_name: 'Matcha Latte Ice', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.sudirman', category_name: 'Minuman', current_stock: 70, stock_active: true, total_qty: Math.round(68 * scaleFactor), revenue: Math.round(1904000 * scaleFactor) },
      { product_id: 'p-10', product_name: 'Tahu Crispy Cabe Garam', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.bogor', category_name: 'Snack & Cemilan', current_stock: 80, stock_active: true, total_qty: Math.round(62 * scaleFactor), revenue: Math.round(930000 * scaleFactor) },
    ]

    const least_products: ProductSalesRankDto[] = [
      { product_id: 'p-11', product_name: 'Kopi Tubruk Robusta', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.bandung', category_name: 'Minuman', current_stock: 18, stock_active: true, total_qty: 0, revenue: 0 },
      { product_id: 'p-12', product_name: 'Es Kelapa Jeruk Nipis', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.bogor', category_name: 'Minuman', current_stock: 22, stock_active: true, total_qty: 0, revenue: 0 },
      { product_id: 'p-13', product_name: 'Salad Buah Saus Keju', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.sudirman', category_name: 'Dessert', current_stock: 14, stock_active: true, total_qty: 0, revenue: 0 },
      { product_id: 'p-14', product_name: 'Tumis Kangkung Belacan', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.bogor', category_name: 'Makanan Utama', current_stock: 10, stock_active: true, total_qty: 1, revenue: 22000 },
      { product_id: 'p-15', product_name: 'Roti Panggang Srikaya', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.bandung', category_name: 'Snack & Cemilan', current_stock: 30, stock_active: true, total_qty: 1, revenue: 18000 },
      { product_id: 'p-16', product_name: 'Jus Alpukat Tanpa Gula', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.sudirman', category_name: 'Minuman', current_stock: 15, stock_active: true, total_qty: 2, revenue: 50000 },
      { product_id: 'p-17', product_name: 'Sate Jamur Tiram', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.bogor', category_name: 'Makanan Utama', current_stock: 8, stock_active: true, total_qty: 2, revenue: 44000 },
      { product_id: 'p-18', product_name: 'Cireng Bumbu Rujak', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.bandung', category_name: 'Snack & Cemilan', current_stock: 45, stock_active: true, total_qty: 3, revenue: 45000 },
      { product_id: 'p-19', product_name: 'Puding Coklat Vla', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.sudirman', category_name: 'Dessert', current_stock: 12, stock_active: true, total_qty: 3, revenue: 42000 },
      { product_id: 'p-20', product_name: 'Wedang Jahe Madu', outlet_name: selectedOutletId ? defaultOutletName : 'BBS cab.bogor', category_name: 'Minuman', current_stock: 25, stock_active: true, total_qty: 4, revenue: 64000 },
    ]

    return {
      filter: {
        period: periodKey,
        start_date: filter?.startDate,
        end_date: filter?.endDate,
        outlet_id: selectedOutletId,
      },
      summary: {
        total_revenue: totalRevenue,
        revenue_growth_percent: selectedOutletId ? 9.4 : 12.4,
        revenue_trend: 'up',

        total_transactions: totalTransactions,
        transaction_growth_percent: selectedOutletId ? 6.8 : 8.1,
        transaction_trend: 'up',

        products_sold: {
          total_qty: totalProductsSold,
          single_product_qty: singleQty,
          package_qty: packageQty,
          package_component_qty: componentQty,
          growth_percent: 5.6,
          trend: 'up',
        },
      },
      revenue_trend: revenueTrend,
      category_sales: categorySales,
      outlet_sales: outletSales,
      hourly_sales: hourlySales,
      top_products: top_products,
      least_products: least_products,
    }
  },

  revenueSeries(
    range: DateRange,
    customDays?: number,
    outletId?: string | null,
  ): RevenuePointDto[] {
    return this.overview({ period: range, outletId }).revenue_trend
  },

  metrics(range: DateRange): KpiMetricDto[] {
    const ov = this.overview({ period: range })
    const avgOrder = Math.round(
      ov.summary.total_revenue / Math.max(ov.summary.total_transactions, 1),
    )
    return [
      {
        id: 'revenue',
        label: 'Total Penjualan',
        value: ov.summary.total_revenue,
        format: 'currency',
        delta_percent: ov.summary.revenue_growth_percent,
      },
      {
        id: 'transactions',
        label: 'Transaksi',
        value: ov.summary.total_transactions,
        format: 'number',
        delta_percent: ov.summary.transaction_growth_percent,
      },
      {
        id: 'avg-order',
        label: 'Rata-rata Order',
        value: avgOrder,
        format: 'currency',
        delta_percent: 3.9,
      },
      {
        id: 'conversion',
        label: 'Konversi Kasir',
        value: 68,
        format: 'percent',
        delta_percent: 2.1,
      },
    ]
  },

  categorySales(
    range: DateRange,
    customDays?: number,
    outletId?: string | null,
  ): CategorySalesDto[] {
    return this.overview({ period: range, outletId }).category_sales
  },

  salesByOutlet(range: DateRange, customDays?: number, outletId?: string | null): OutletSalesDto[] {
    return this.overview({ period: range, outletId }).outlet_sales
  },

  recentOrders(range: DateRange): OrderDto[] {
    const days = RANGE_DAYS[range] ?? 1
    const rand = seeded(days * 11)
    const now = new Date()
    return Array.from({ length: 24 }, (_, i) => {
      const created = new Date(now.getTime() - i * (1000 * 60 * (18 + rand() * 40)))
      const itemCount = 1 + Math.floor(rand() * 8)
      return {
        id: `ord_${(1000 + i).toString()}`,
        invoice_no: `INV-2608-${(240 - i).toString().padStart(4, '0')}`,
        customer: CUSTOMERS[Math.floor(rand() * CUSTOMERS.length)],
        created_at: created.toISOString(),
        item_count: itemCount,
        total: Math.round(itemCount * (35_000 + rand() * 55_000)),
        status: STATUSES[Math.floor(rand() * STATUSES.length)],
        payment: PAYMENTS[Math.floor(rand() * PAYMENTS.length)],
      }
    })
  },
}
